import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, CheckCircle2, ShieldCheck, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ToolkitCard from "@/components/ToolkitCard";
import { FadeIn, Eyebrow } from "@/components/Reveal";
import { api, aud } from "@/lib/api";
import BrandSignoff from "@/components/BrandSignoff";
import { IMAGES } from "@/data/site";
import Seo from "@/components/Seo";

const DetailModal = ({ product, onClose, onAdd, added }) => (
  <motion.div
    className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-6"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    data-testid="toolkit-detail-modal"
  >
    <div className="absolute inset-0 bg-teal-dark/70 backdrop-blur-sm" onClick={onClose} />
    <motion.div
      className="relative bg-ivory w-full max-w-3xl max-h-[90vh] overflow-y-auto"
      initial={{ y: 40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 40, opacity: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 bg-ivory p-2 text-teal hover:bg-sand-light transition-colors"
        data-testid="toolkit-detail-close"
        aria-label="Close"
      >
        <X className="w-5 h-5" />
      </button>
      <div className="grid sm:grid-cols-2">
        <div className="aspect-[4/3] sm:aspect-auto sm:h-full bg-sand-light">
          <img
            src={IMAGES[product.image]}
            alt={product.title}
            className={`w-full h-full ${
              product.image.endsWith("_cover") ? "object-contain" : "object-cover"
            }`}
          />
        </div>
        <div className="p-8">
          <span className="text-[11px] font-mono uppercase tracking-[0.16em] text-teal/60">
            {product.tag} · {product.available ? "Instant PDF download" : "Coming soon"}
          </span>
          <h2 className="mt-3 font-serif text-2xl text-teal tracking-tight">{product.title}</h2>
          <p className="mt-3 text-sm text-charcoal/75 leading-relaxed">{product.description}</p>
          <p className="mt-4 text-sm text-charcoal/75">
            <span className="font-semibold text-teal">Designed for:</span> {product.audience}
          </p>
          <p className="mt-5 text-xs font-mono uppercase tracking-[0.16em] text-teal">
            What's inside
          </p>
          <ul className="mt-2 space-y-2">
            {product.includes.map((item) => (
              <li key={item} className="flex gap-2 text-sm text-charcoal/80">
                <CheckCircle2 className="w-4 h-4 text-seaglass shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
          <div className="mt-5 bg-sand-light/60 border border-cloud px-4 py-3" data-testid="toolkit-workbook-notice">
            <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-teal">About this resource</p>
            <p className="mt-1.5 text-xs text-charcoal/70 leading-relaxed">
              An educational preparation resource — it helps you organise information, identify
              planning considerations and prepare questions for the appropriate authorities or
              professional advisers. It isn't a professional assessment, formal due diligence report
              or compliance certificate, and completing it doesn't establish that a development is
              permissible, compliant or approved.{" "}
              <Link to="/legal/disclaimer" className="text-teal underline underline-offset-2">Learn more</Link>
            </p>
          </div>
          <div className="mt-6 flex items-center justify-between">
            <span className="font-serif text-3xl text-teal">{aud(product.price)}</span>
            <button
              onClick={() => {
                if (!product.available) return;
                onAdd(product);
                onClose();
              }}
              disabled={!product.available}
              data-testid={`toolkit-modal-add-${product.id}`}
              className={`px-6 py-3 font-medium text-sm rounded-full transition-colors ${
                !product.available
                  ? "bg-sand-light text-charcoal/45 cursor-not-allowed"
                  : added
                  ? "bg-seaglass-light text-teal"
                  : "bg-teal text-ivory hover:bg-teal-dark"
              }`}
            >
              {!product.available ? "Available soon" : added ? "Added to bundle" : "Add to bundle"}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  </motion.div>
);

const Toolkits = () => {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [detail, setDetail] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    api
      .get("/products")
      .then(({ data }) =>
        setProducts([...data].sort((a, b) => Number(b.available) - Number(a.available)))
      )
      .catch(() => toast.error("Could not load the store. Please refresh."))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (p) => {
    if (!p.available) return;
    setSelected((prev) =>
      prev.find((x) => x.id === p.id) ? prev.filter((x) => x.id !== p.id) : [...prev, p]
    );
  };

  const total = useMemo(() => selected.reduce((s, p) => s + p.price, 0), [selected]);

  const checkout = async () => {
    if (!selected.length || checkingOut) return;
    setCheckingOut(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: selected.map((p) => p.id),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Checkout could not be started. Please try again.");
      setCheckingOut(false);
    }
  };

  return (
    <div data-testid="toolkits-page">
      <Seo
        title="Planning Toolkits & Digital Store"
        description="Downloadable NSW planning toolkits — plain-English guides, fillable workbooks and printable checklists for due diligence, renovations, pools, dual occupancy and more."
      />
      <section className="bg-sand-light py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Eyebrow>Toolkits &amp; Digital Store</Eyebrow>
          <h1 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-teal">
            Practical planning knowledge, instantly downloadable
          </h1>
          <p className="mt-5 max-w-2xl text-charcoal/70 leading-relaxed">
            Each toolkit combines a plain-English explanation with worksheets, investigation
            exercises and checklists. Add one — or bundle several — and download immediately
            after secure checkout.
          </p>
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-xs font-mono uppercase tracking-[0.18em] text-charcoal/55">
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Secure Stripe checkout
            </span>
            <span className="inline-flex items-center gap-2">
              <Download className="w-4 h-4" /> Instant PDF delivery
            </span>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 pb-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="py-24 flex justify-center">
              <Loader2 className="w-8 h-8 text-teal animate-spin" />
            </div>
          ) : (
            <>
              <div className="mb-8 flex flex-wrap gap-2" data-testid="toolkit-filters">
                {[
                  ["all", `All toolkits (${products.length})`],
                  ["available", `Available now (${products.filter((p) => p.available).length})`],
                  ["coming", `Coming soon (${products.filter((p) => !p.available).length})`],
                ].map(([v, label]) => (
                  <button
                    key={v}
                    onClick={() => setFilter(v)}
                    data-testid={`toolkit-filter-${v}`}
                    className={`text-xs font-mono uppercase tracking-[0.12em] px-4 py-2.5 border rounded-full transition-colors ${
                      filter === v
                        ? "bg-teal text-ivory border-teal"
                        : "bg-white border-cloud text-charcoal/70 hover:border-teal"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="toolkit-grid">
                {products
                  .filter((p) => filter === "all" || (filter === "available") === p.available)
                  .map((p, i) => (
                    <ToolkitCard
                      key={p.id}
                      product={p}
                      index={i}
                      onDetails={setDetail}
                  onAdd={toggle}
                  added={!!selected.find((x) => x.id === p.id)}
                />
                  ))}
              </div>
            </>
          )}
          <FadeIn className="mt-14">
            <p className="max-w-3xl text-xs text-charcoal/60 leading-relaxed">
              Toolkits provide general educational information, not property-specific advice.
              Planning requirements vary by location and proposal, and purchasing a toolkit does
              not guarantee approval. Prices in AUD, GST inclusive.
            </p>
          </FadeIn>
        </div>
      </section>

      <AnimatePresence>
        {selected.length > 0 && (
          <motion.div
            className="fixed bottom-0 inset-x-0 z-50"
            initial={{ y: 90 }}
            animate={{ y: 0 }}
            exit={{ y: 90 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            data-testid="bundle-bar"
          >
            <div className="max-w-3xl mx-auto px-4 pb-5">
              <div className="bg-teal-dark text-ivory px-6 py-4 flex items-center justify-between gap-4 shadow-2xl border border-sand/30">
                <div className="text-sm">
                  <span className="font-semibold">{selected.length}</span> toolkit
                  {selected.length > 1 ? "s" : ""} ·{" "}
                  <span className="font-serif text-lg">{aud(total)}</span>
                </div>
                <button
                  onClick={checkout}
                  disabled={checkingOut}
                  data-testid="checkout-button"
                  className="inline-flex items-center gap-2 bg-sand text-teal-dark font-semibold px-6 py-3 rounded-full hover:bg-ivory transition-colors disabled:opacity-60"
                >
                  {checkingOut ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Opening checkout…
                    </>
                  ) : (
                    "Checkout securely"
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {detail && (
          <DetailModal
            product={detail}
            onClose={() => setDetail(null)}
            onAdd={toggle}
            added={!!selected.find((x) => x.id === detail.id)}
          />
        )}
      </AnimatePresence>

      <BrandSignoff />
    </div>
  );
};

export default Toolkits;
